import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { Banknote, Calendar, TrendingUp, Users } from "lucide-react";
import React from "react";
import { useOutstandingSummary } from "../../hooks";

// Helper function to safely convert to number
const safeNumber = (value: any): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const OutstandingSummary: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: summaryData } = useOutstandingSummary(Number(remoteUtilityId));
  const currency = useCurrency();
  const summaryCards = [
    {
      title: "Total Outstanding",
      tooltip: "Total unpaid amount pending from all accounts.",
      value: safeNumber(summaryData?.totalOutstandingBalance),
      displayValue: summaryData?.totalOutstandingBalance
        ? `${currency} ${safeNumber(
            summaryData.totalOutstandingBalance,
          ).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency} 0.00`,
      trend: "up" as const,
      icon: <Banknote className="h-8 w-8 text-green-500" />,
      color: "text-green-600",
    },
    {
      title: "Below 30 days",
      tooltip: "The total outstanding pending from less than 30 days.",
      value: safeNumber(summaryData?.["30"]),
      displayValue: summaryData?.["30"]
        ? safeNumber(summaryData["30"]).toLocaleString()
        : "0",
      trend: "up" as const,
      icon: <Users className="h-8 w-8 text-blue-500" />,
      color: "text-blue-600",
    },
    {
      title: "30-60 days",
      tooltip:
        "The total outstanding pending greater than equal to 30 days and less than equal to 60 days.",
      value: safeNumber(summaryData?.["60"]),
      displayValue: summaryData?.["60"]
        ? safeNumber(summaryData["60"]).toLocaleString()
        : "0",
      trend: "up" as const,
      icon: <Calendar className="h-8 w-8 text-orange-500" />,
      color: "text-orange-600",
    },
    {
      title: "60-90 Days",
      tooltip:
        "The total outstanding pending greater than equal to 61 days and less than equal to 90 days.",
      value: safeNumber(summaryData?.["90"]),
      displayValue: summaryData?.["90"]
        ? safeNumber(summaryData["90"]).toLocaleString()
        : "0",
      trend: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-purple-500" />,
      color: "text-purple-600",
    },
    {
      title: "90 Days",
      tooltip: "The total outstanding pending greater than 90 days",
      value: safeNumber(summaryData?.[">90"]),
      displayValue: summaryData?.[">90"]
        ? safeNumber(summaryData[">90"]).toLocaleString()
        : "0",
      trend: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-purple-500" />,
      color: "text-purple-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {summaryCards.map((card, index) => (
        <QuickStatsCard
          key={index}
          title={card.title}
          count={card.value}
          displayValue={card.displayValue}
          trendDirection={card.trend}
          // trendSummary={card.change}
          icon={card.icon}
          tooltip={card.tooltip}
        />
      ))}
    </div>
  );
};
